import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import {
  isSpreadsheetCsvFile,
  isSpreadsheetExcelFile,
  readCsvFile,
  readExcelFile,
} from "@lhd/utility";
import Button from "@lhd/components/Button";
import { message, Upload } from "antd";
import type { UploadProps } from "antd";
import { useMemo } from "react";
import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";

const SPREADSHEET_ACCEPT =
  ".csv,.xlsx,.xls,.xlsm,text/csv,application/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

interface IProps {
  handleImportData: (rows: string[][]) => void;
  appParts?: AppPartType[];
  isDisabled?: boolean;
}

const ButtonImport = (props: IProps) => {
  const { handleImportData, appParts, isDisabled } = props;

  const columnParts = useMemo(
    () =>
      [...(appParts || [])].sort(
        (a, b) => a.item_config_data.x - b.item_config_data.x,
      ),
    [appParts],
  );

  if (!columnParts.length) return null;

  const validateCell = (cell: string, appPart?: AppPartType) => {
    if (!appPart) return true;
    const isRequired = appPart.is_required_field;
    const name = appPart.name;
    if (isRequired && !cell) {
      message.error(`${name}は必須項目です。`);
      return false;
    }

    const minChar = appPart.min_num_char;
    if (minChar && cell.length < minChar) {
      message.error(`${name}は${minChar}文字以上で入力してください。`);
      return false;
    }
    const maxChar = appPart.max_num_char;

    if (maxChar && cell.length > maxChar) {
      message.error(`${name}は${maxChar}文字以内で入力してください。`);
      return false;
    }

    if (appPart.app_part_type === APP_PART_TYPES.NUMBER) {
      if (isNaN(Number(cell.replaceAll(appPart.unit_of_measure, "")))) {
        message.error(`${name}は数値で入力してください。`);
        return false;
      }
    }
    return true;
  };

  const validateCells = (rows: string[][]): boolean => {
    for (const row of rows) {
      for (let i = 0; i < row.length; i++) {
        if (!validateCell(row[i] ?? "", columnParts[i])) {
          return false;
        }
      }
    }
    return true;
  };

  const beforeUpload: UploadProps["beforeUpload"] = async (file) => {
    const f = file as File;
    if (isSpreadsheetCsvFile(f)) {
      try {
        const [headerRow, ...rows] = await readCsvFile(f);
        if (validateCells(rows)) {
          handleImportData(rows);
        }
      } catch {
        message.error("CSVの読み込みに失敗しました");
      }
      return false;
    }

    if (isSpreadsheetExcelFile(f)) {
      try {
        const [headerRow, ...rows] = await readExcelFile(f);
        if (validateCells(rows)) {
          handleImportData(rows);
        }
      } catch {
        message.error("Excelの読み込みに失敗しました");
      }
      return false;
    }

    message.error("CSVまたはExcelファイルのみアップロードできます");
    return Upload.LIST_IGNORE;
  };

  return (
    <Upload
      accept={SPREADSHEET_ACCEPT}
      beforeUpload={beforeUpload}
      showUploadList={false}
    >
      <Button
        label="Excel・CSVのインポート"
        height={20}
        disabled={isDisabled}
      />
    </Upload>
  );
};

export default ButtonImport;
